// src/page/SpotList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function SpotList() {
    useAuthGuard();
    const navigate = useNavigate();
    const location = useLocation();

    const [sp, setSp] = useSearchParams();
    const initPage = Number(sp.get('page') || 1);
    const initSize = Number(sp.get('size') || 20);
    const initQ = sp.get('q') || '';
    const initTab = sp.has('tab') ? Number(sp.get('tab')) : -1;
    const initSList = sp.get('s')
        ? sp.get('s').split(',').map(Number).filter(n => [0, 1, 2, 3].includes(n))
        : [];
    const initSortKey = sp.get('sort') || 'start';
    const initSortDir = sp.get('dir') || 'desc';

    const [keywordInput, setKeywordInput] = useState(initQ);
    const [searchKeyword, setSearchKeyword] = useState(initQ);

    const [statusChecks, setStatusChecks] = useState({
        pre: initSList.includes(0),
        active: initSList.includes(1),
        ended: initSList.includes(2),
        unpublished: initSList.includes(3),
    });

    const [s, setS] = useState(initTab);
    const [querySList, setQuerySList] = useState(initSList);

    const [sort, setSort] = useState({ key: initSortKey, dir: initSortDir });
    const toggleSort = (key) =>
        setSort(prev =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
        );

    const [page, setPage] = useState(initPage);
    const [pageSize, setPageSize] = useState(initSize);

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [headerCounts, setHeaderCounts] = useState({ all: 0, pre: 0, active: 0, ended: 0, unpublished: 0 });

    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

    const DEFAULT_SORT = { key: 'start', dir: 'desc' };

    useEffect(() => {
        const noQuery = !new URLSearchParams(location.search).toString();
        if (!noQuery) return;
        const alreadyDefault =
            page === 1 &&
            pageSize === 20 &&
            searchKeyword.trim() === '' &&
            querySList.length === 0 &&
            s === -1 &&
            sort.key === DEFAULT_SORT.key &&
            sort.dir === DEFAULT_SORT.dir;
        if (alreadyDefault) return;

        setKeywordInput('');
        setSearchKeyword('');
        setStatusChecks({ pre: false, active: false, ended: false, unpublished: false });
        setQuerySList([]);
        setS(-1);
        setSort(DEFAULT_SORT);
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const p = new URLSearchParams();

        if (page !== 1) p.set('page', String(page));
        if (pageSize !== 20) p.set('size', String(pageSize));
        if (searchKeyword) p.set('q', searchKeyword);

        if (querySList.length) p.set('s', querySList.join(','));
        else if (s >= 0) p.set('tab', String(s));

        if (sort.key !== DEFAULT_SORT.key) p.set('sort', sort.key);
        if (sort.dir !== DEFAULT_SORT.dir) p.set('dir', sort.dir);

        setSp(p, { replace: true });
    }, [page, pageSize, searchKeyword, querySList, s, sort, setSp]);

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token');

                const url = new URL(`${API_BASE_URL}/v1/spot`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);

                if (querySList.length > 0) url.searchParams.set('s', querySList.join(','));
                else if (s >= 0) url.searchParams.set('s', String(s));

                if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());

                const res = await fetch(url.toString(), {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(Number(data.total) || 0);

                if (data.counts) {
                    const noFilter = searchKeyword.trim() === '' && querySList.length === 0 && s === -1;
                    if (noFilter) setHeaderCounts(data.counts);
                }

                if (data.size && Number(data.size) !== pageSize) setPageSize(Number(data.size));
            } catch (e) {
                if (e.name !== 'AbortError') setError('スポットの取得に失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [API_BASE_URL, page, pageSize, s, searchKeyword, querySList, sort]);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    const activateTab = (nextS) => {
        if (nextS >= 0) {
            const nextChecks = { pre: false, active: false, ended: false, unpublished: false };
            if (nextS === 0) nextChecks.pre = true;
            if (nextS === 1) nextChecks.active = true;
            if (nextS === 2) nextChecks.ended = true;
            if (nextS === 3) nextChecks.unpublished = true;
            setStatusChecks(nextChecks);
            setQuerySList([nextS]);
        } else {
            setStatusChecks({ pre: false, active: false, ended: false, unpublished: false });
            setQuerySList([]);
        }
        setS(nextS);
        setPage(1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSearchKeyword(keywordInput);
        const selected = [];
        if (statusChecks.pre) selected.push(0);
        if (statusChecks.active) selected.push(1);
        if (statusChecks.ended) selected.push(2);
        if (statusChecks.unpublished) selected.push(3);
        setQuerySList(selected);
        setS(selected.length === 1 ? selected[0] : -1);
        setPage(1);
    };

    const handleClear = () => {
        setKeywordInput('');
        setSearchKeyword('');
        setStatusChecks({ pre: false, active: false, ended: false, unpublished: false });
        setQuerySList([]);
        setS(-1);
        setPage(1);
        setSort({ key: 'start', dir: 'desc' });
    };

    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (s) => {
        if (!s) return '-';
        const d = new Date(s);
        if (isNaN(d)) return s;
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const statusLabel = (it) => {
        const pub = !!it.publishedFlag;
        if (!pub) return '非公開';
        const now = new Date();
        const start = new Date(it.publishedStartDate);
        const end = new Date(it.publishedEndDate);
        if (now < start) return '公開前';
        if (now > end) return '公開終了';
        return '公開中';
    };

    // ---- CSV Export ----
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';

            const url = new URL(`${API_BASE_URL}/v1/spot/export`);
            // same filters as list (omit paging to export all)
            if (querySList.length > 0) url.searchParams.set('s', querySList.join(','));
            else if (s >= 0) url.searchParams.set('s', String(s));
            if (searchKeyword.trim() !== '') url.searchParams.set('q', searchKeyword.trim());
            url.searchParams.set('sort', sort.key);
            url.searchParams.set('dir', sort.dir);

            const res = await fetch(url.toString(), {
                headers: {
                    Accept: 'text/csv',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            if (!res.ok) {
                const t = await res.text().catch(() => '');
                throw new Error(t || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const a = document.createElement('a');
            const now = new Date();
            const ts = [
                now.getFullYear(),
                pad(now.getMonth() + 1),
                pad(now.getDate()),
                pad(now.getHours()),
                pad(now.getMinutes()),
                pad(now.getSeconds()),
            ].join('');
            a.href = URL.createObjectURL(blob);
            a.download = `スポット一覧_${ts}.csv`;
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(a.href);
            document.body.removeChild(a);
        } catch (e) {
            alert(`CSVエクスポートに失敗しました: ${e.message || e}`);
        }
    };

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/spot">スポット管理</a></li>
                    <li className="breadcrumb-item active">スポット一覧</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">スポット一覧</h2>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-links mb-3 mb-lg-2 mx-n3">
                    <li className="nav-item">
                        <a
                            href="#"
                            aria-current={s === -1 ? 'page' : undefined}
                            className={`nav-link ${s === -1 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(-1); }}>
                            <span>全て </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.all})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#" className={`nav-link ${s === 0 ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); activateTab(0); }}>
                            <span>公開前 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.pre})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#" className={`nav-link ${s === 1 ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); activateTab(1); }}>
                            <span>公開中 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.active})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#" className={`nav-link ${s === 2 ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); activateTab(2); }}>
                            <span>公開終了 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.ended})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#" className={`nav-link ${s === 3 ? 'active' : ''}`} onClick={(e) => { e.preventDefault(); activateTab(3); }}>
                            <span>非公開 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.unpublished})</span>
                        </a>
                    </li>
                </ul>

                <div id="products">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div className="search-box">
                                    <input
                                        aria-label="Search"
                                        className="form-control search-input search"
                                        placeholder="ID/スポット名"
                                        type="search"
                                        name="keyword"
                                        value={keywordInput}
                                        onChange={(e) => setKeywordInput(e.target.value)}
                                    />
                                    <span className="fas fa-search search-box-icon"></span>
                                </div>

                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="status1"
                                                checked={statusChecks.pre}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, pre: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="status1">公開前</label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="status2"
                                                checked={statusChecks.active}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, active: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="status2">公開中</label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="status3"
                                                checked={statusChecks.ended}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, ended: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="status3">公開終了</label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="status4"
                                                checked={statusChecks.unpublished}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, unpublished: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="status4">非公開</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        id="addBtn"
                                        type="button"
                                        onClick={() => navigate('/spot/new')}
                                    >
                                        <span className="fas fa-plus me-2"></span>新規登録
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="white-space-nowrap fs-9 align-middle ps-0" style={{ maxWidth: '20px', width: '18px' }}>
                                            <div className="form-check mb-0 fs-8" style={{ display: 'none' }}>
                                                <input className="form-check-input" id="checkbox-bulk-products-select" type="checkbox" />
                                            </div>
                                        </th>
                                        <th className="sort align-middle ps-3">スポットID</th>
                                        <th className="sort align-middle ps-3" role="button" onClick={() => { toggleSort('title'); setPage(1); }}>
                                            スポット名 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="sort align-middle ps-3" role="button" onClick={() => { toggleSort('order'); setPage(1); }}>
                                            表示順 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="sort align-middle ps-3" role="button" onClick={() => { toggleSort('start'); setPage(1); }}>
                                            公開開始日時 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="sort align-middle ps-3" role="button" onClick={() => { toggleSort('end'); setPage(1); }}>
                                            公開終了日時 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="sort align-middle ps-3">ステータス</th>
                                        <th className="sort align-middle ps-3"></th>
                                    </tr>
                                </thead>

                                <tbody className="list" id="products-table-body">
                                    {loading && (<tr><td colSpan={8} className="text-center py-4">読み込み中...</td></tr>)}
                                    {!loading && error && (<tr><td colSpan={8} className="text-danger py-4">{error}</td></tr>)}
                                    {!loading && !error && items.length === 0 && (<tr><td colSpan={8} className="text-center py-4">データがありません</td></tr>)}
                                    {!loading && !error && items.map((it) => (
                                        <tr key={it.spotId}>
                                            <td className="fs-9 align-middle">
                                                <div className="form-check mb-0 fs-8" style={{ display: 'none' }}>
                                                    <input className="form-check-input" type="checkbox" />
                                                </div>
                                            </td>
                                            <td>{it.spotId}</td>
                                            <td>{it.spotName}</td>
                                            <td>{it.displayOrder ?? ''}</td>
                                            <td>{fmt(it.publishedStartDate)}</td>
                                            <td>{fmt(it.publishedEndDate)}</td>
                                            <td>{statusLabel(it)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => navigate(`/spot/${it.spotId}/edit`)}
                                                    type="button"
                                                >
                                                    編集
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>

                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>

                                <ul className="mb-0 pagination">
                                    {pages.map(p => (
                                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
